import { styled } from '@mui/material';
import { AgChartsCommunityModule } from 'ag-charts-community';
import type { FillOperationParams } from 'ag-grid-community';
import {
    AllCommunityModule,
    CellClassParams,
    CellValueChangedEvent,
    ClientSideRowModelModule,
    ColDef,
    ColumnState,
    DisplayedColumnsChangedEvent,
    GetRowIdParams,
    GetRowNodeIdFunc,
    GridApi,
    GridReadyEvent,
    ModelUpdatedEvent,
    ModuleRegistry,
    ProcessCellForExportParams,
    ProcessDataFromClipboardParams,
    provideGlobalGridOptions,
    ValidationModule,
} from 'ag-grid-community';
import {
    AdvancedFilterModule,
    CellSelectionModule,
    ClipboardModule,
    ColumnMenuModule,
    ColumnsToolPanelModule,
    ContextMenuModule,
    ExcelExportModule,
    FiltersToolPanelModule,
    GroupFilterModule,
    PivotModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    SetFilterModule,
    SideBarModule,
    SparklinesModule,
    StatusBarModule,
    TreeDataModule,
} from 'ag-grid-enterprise';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { equals } from 'ramda';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { DataTableEditedEvent } from 'src/domain/events/data-table-edit.event';
import { useBufferTime } from 'src/hooks/useBufferTime';
import { useOnResize } from 'src/hooks/useOnResize';
import { useContextMenuItems } from 'src/utils/ag-grid/getContextMenuItems';
import { formatNumber, parseNumber } from 'src/utils/formatNumber';
import { DefaultColumnsOverview } from './columns/default-columns-overview';
import { DataTableLoading } from './DataTableLoading';
import { Cell, CellValidationOptions, Row } from './types';

provideGlobalGridOptions({
    theme: 'legacy',
});

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    AllCommunityModule,
    ClipboardModule,
    CellSelectionModule,
    RowGroupingModule,
    RowGroupingPanelModule,
    GroupFilterModule,
    TreeDataModule,
    PivotModule,
    StatusBarModule,
    ColumnMenuModule,
    ContextMenuModule,
    SideBarModule,
    ColumnsToolPanelModule,
    FiltersToolPanelModule,
    AdvancedFilterModule,
    SetFilterModule,
    SparklinesModule.with(AgChartsCommunityModule),
    ExcelExportModule,
]);

if (import.meta.env.MODE !== 'production') {
    ModuleRegistry.registerModules([ValidationModule]);
}

function mergeColumns(column: ColDef, defaultColDef?: ColDef): ColDef {
    //* Since the column may contain an undefined value, we need to make sure we don't override the defaultColDef with undefined values.
    const mergedColumn = {
        ...defaultColDef,
    };
    Object.keys(column).forEach((key) => {
        const tKey = key as keyof typeof column;
        if (column[tKey]) {
            mergedColumn[tKey] = column[tKey];
        }
    });
    return mergedColumn;
}

export interface DataTableProps extends Omit<AgGridReactProps, 'fillOperation' | 'theme'> {
    rows: Row[] | null | undefined;
    columns?: ColDef[];
    onCellValuesChanged?: (values: Cell[]) => void;
    debounceTime?: number;
    loading?: boolean;
    columnMinWidth?: number;
    columnMaxWidth?: number;
    isNumberTable?: boolean;
    autoSizeToFit?: boolean;
    cellValidationOptions?: CellValidationOptions;
    onColumnStateChanged?: (columnState: ColumnState[]) => void;
    fillOperation?: (params: FillOperationParams) => unknown;
    skipForceRefresh?: boolean;
}

export const DataTable: FC<DataTableProps> = ({
    rows,
    columns = DefaultColumnsOverview,
    onCellValuesChanged,
    debounceTime,
    loading,
    domLayout,
    columnMinWidth = 80,
    columnMaxWidth,
    onGridReady,
    isNumberTable = true,
    suppressAutoSize = true,
    autoSizeToFit = false,
    cellValidationOptions,
    onColumnStateChanged,
    fillOperation,
    skipForceRefresh,
    ...props
}) => {
    const gridRef = useRef<AgGridReact>(null);
    const [bufferedCells, pushToBuffer] = useBufferTime<Cell>(debounceTime ?? 10);

    // resize columns when window size change
    useOnResize(() => {
        if (gridRef.current?.api) {
            sizeColumnsToFit(gridRef.current.api);
        }
    });

    const sizeColumnsToFit = useCallback(
        (api: GridApi) => {
            if (autoSizeToFit) {
                setTimeout(() => {
                    api.sizeColumnsToFit({
                        defaultMinWidth: columnMinWidth,
                        defaultMaxWidth: columnMaxWidth,
                    });
                });
            }
        },
        [autoSizeToFit, columnMaxWidth, columnMinWidth]
    );

    const handleGridReady = useCallback(
        (event: GridReadyEvent) => {
            // Run initialization methods when grid data is first rendered
            sizeColumnsToFit(event.api);

            if (typeof onGridReady === 'function') {
                onGridReady(event);
            }
        },
        [onGridReady, sizeColumnsToFit]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: we need to run this when columns or rows change as well
    useEffect(() => {
        if (gridRef.current?.api) {
            sizeColumnsToFit(gridRef.current.api);
        }
    }, [sizeColumnsToFit, columns, rows]);

    const rowData = useMemo(() => {
        if (rows) {
            return rows;
        }
    }, [rows]);

    // whenever buffer updates, invoke callback
    // biome-ignore lint/correctness/useExhaustiveDependencies: don't react to when onCellValuesChanged prop changes
    useEffect(() => {
        if (bufferedCells.notEmpty()) {
            onCellValuesChanged?.(bufferedCells);

            // fire event that table has been edited
            dispatchEvent(new Event(DataTableEditedEvent));
        }
    }, [bufferedCells]);

    const getRowId = useCallback<GetRowNodeIdFunc>((params: GetRowIdParams<Row>) => {
        // Use rowId as default row id and fallback to id in case rowId is not present
        return (params?.data?.rowId ?? params?.data?.id)?.toString();
    }, []);

    const processDataFromClipboard = useCallback(
        (params: ProcessDataFromClipboardParams) =>
            params.data.map((row) =>
                // seems like AG Grid sanitizes new lines into empty string, so we remove any trailing empty strings
                // ! this could lead to cases where copying actual empty cells that are last will not be copied
                row.last()?.isEmpty() ? row.slice(0, row.length - 1) : row
            ),
        []
    );

    const processCellFromClipboard = useCallback(
        (params: ProcessCellForExportParams) => {
            if (isNumberTable) {
                if (params.value) {
                    const value = params.value.replace('%', '');
                    const valueAsNumber = parseNumber(value);
                    // We verify that it is actually a number, but we need to return the formatted number value as pasted.
                    if (!Number.isNaN(valueAsNumber)) return value;
                }
                return 0;
            }
            try {
                return JSON.parse(params.value ?? '');
            } catch {
                return params.parseValue(params.value);
            }
        },
        [isNumberTable]
    );

    const processCellForClipboard = useCallback(
        (params: ProcessCellForExportParams) => {
            /**
             * Value is either primitive or a tuple (used in Gross Margin)
             * @see {@link GrossMargin}
             */
            const value = params.value as unknown;

            let parsedValue: unknown;

            // assume predicted value is second, if not present default to current value
            if (Array.isArray(value)) {
                parsedValue = value[1] ?? value[0];
            } else {
                parsedValue = value;
            }

            if (isNumberTable) {
                if (typeof parsedValue === 'number') {
                    parsedValue = formatNumber(parsedValue);
                }
                return typeof parsedValue === 'string' ? formatNumber(parseNumber(parsedValue)) : '0';
            }
            return JSON.stringify(parsedValue ?? '');
        },
        [isNumberTable]
    );

    const onCellChange = useCallback(
        (event: CellValueChangedEvent) => {
            // we need to remove all white spaces and set correct delimiter
            const parsedOldValue = isNumberTable ? parseNumber(event.oldValue?.toString()) : event.oldValue;
            const parsedNewValue = isNumberTable ? parseNumber(event.newValue?.toString()) : event.newValue;

            // ignore if actual value has not changed - handle both objects and primitives
            if (equals(parsedOldValue, parsedNewValue)) return;

            const rowId = event.node?.id ?? event.data?.rowId ?? event.data?.id;
            const column = event.colDef.field;
            const category = event.data?.category;
            const footer: string | undefined = event.data?.footer ? event.data.subCategory : undefined;
            const cell: Cell<unknown> = {
                rowId,
                value: parsedNewValue,
                column,
                footer,
                category,
            };

            pushToBuffer(cell);
        },
        [isNumberTable, pushToBuffer]
    );

    const columnDefs: ColDef[] = useMemo(() => {
        return columns.map((column) => {
            //* Due to an unknown issue after upgrading AG Grid, it seems the value formatter from the defaultColDef is not applied, so we just merge them manually here.
            column = mergeColumns(column, props.defaultColDef);

            if (
                !cellValidationOptions?.columns ||
                (Array.isArray(cellValidationOptions.columns) &&
                    !cellValidationOptions.columns.includes(column.field ?? ''))
            ) {
                return column;
            }

            return {
                ...column,
                autoHeaderHeight: true,
                cellClassRules: {
                    'ag-cell--warning': (params: CellClassParams): boolean => {
                        if (typeof cellValidationOptions.validationFunc === 'function') {
                            return cellValidationOptions.validationFunc(params);
                        }
                        return false;
                    },
                    ...column.cellClassRules,
                },
            };
        });
    }, [cellValidationOptions, columns, props.defaultColDef]);

    const saveColumnState = useCallback(
        ({ api, source }: DisplayedColumnsChangedEvent) => {
            if (['gridOptionsChanged'].includes(source)) return;
            if (typeof onColumnStateChanged === 'function') {
                onColumnStateChanged(structuredClone(api.getColumnState()));
            }
        },
        [onColumnStateChanged]
    );

    const onModelUpdated = useCallback(
        ({ api }: ModelUpdatedEvent) => {
            if (!skipForceRefresh) {
                api.refreshCells({ force: true });
            }
        },
        [skipForceRefresh]
    );

    const getContextMenuItems = useContextMenuItems();

    return (
        <StyledAGGridReact
            enableFilterHandlers
            getContextMenuItems={getContextMenuItems}
            {...props}
            loading={loading}
            onModelUpdated={onModelUpdated}
            ref={gridRef}
            rowData={rowData}
            cellSelection={{
                handle: {
                    mode: 'fill',
                    direction: 'xy',
                    setFillValue: fillOperation,
                },
                suppressMultiRanges: true,
            }}
            columnDefs={columnDefs}
            getRowId={getRowId}
            onCellValueChanged={onCellChange}
            onGridReady={handleGridReady}
            onDisplayedColumnsChanged={saveColumnState}
            processCellForClipboard={props.processCellForClipboard ?? processCellForClipboard}
            enterNavigatesVerticallyAfterEdit
            domLayout={domLayout ?? 'autoHeight'}
            processCellFromClipboard={processCellFromClipboard}
            processDataFromClipboard={processDataFromClipboard}
            tooltipShowDelay={500}
            tooltipHideDelay={10000}
            loadingOverlayComponent={DataTableLoading}
            suppressAutoSize={suppressAutoSize}
        />
    );
};

export const StyledAGGridReact = styled(AgGridReact)(({ theme }) => ({
    '.ag-root-wrapper': {
        color: theme.palette.text.primary,
        borderRadius: 5,
        border: 'none',
    },

    '.ag-header': {
        borderBottom: '2px solid',
        borderColor: theme.palette.common.white,
    },
    '.ag-header-cell': {
        backgroundColor: theme.palette.grey[200],
        borderRight: '2px solid',
        borderColor: theme.palette.common.white,
        color: theme.palette.text.primary,
    },
    '.ag-cell-value': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    '.ag-cell, .ag-cell-label-container, .ag-center-cols-container > .ag-row > .ag-cell:first-of-type, .ag-pinned-left-cols-container .ag-row .ag-cell:first-of-type, .ag-pinned-right-cols-container .ag-row .ag-cell:first-of-type':
        {
            fontSize: 14,
        },
    '.ag-row': {
        border: 'none',
    },
    '.ag-row .ag-cell': {
        borderRight: '2px solid',
        borderColor: theme.palette.common.white,
        color: theme.palette.text.primary,
        borderBottom: 'none',
        borderTop: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'right',
    },
    '.ag-row .ag-cell[aria-colindex="1"]': {
        justifyContent: 'left',
        fontWeight: 'bold',
    },
    '.ag-row .ag-cell:not([aria-colindex="1"]):last-child, .ag-header-cell:not([aria-colindex="1"]):last-child': {
        borderRight: '1px solid transparent',
    },
    '.ag-root-wrapper .ag-row .ag-cell-range-single-cell.ag-cell-range-handle': {
        borderRight: '1px solid',
        borderColor: theme.palette.info.main,
    },
    '.ag-pinned-left-header': {
        border: 'none',
    },
    '.ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right):not(.ag-cell-range-single-cell)': {
        borderRight: '2px solid',
        borderColor: theme.palette.common.white,
    },
    '.ag-horizontal-left-spacer': {
        display: 'table',
    },
}));
